import React, { useState, useMemo, useRef, useEffect } from 'react';
import './PracticesPage.css';
import PracticeSetupWizard from '../components/PracticeSetupWizard';

function PracticesPage({ practices, onSelectPractice, onPracticesUpdated, onGoToAdmin, userRole, canOnboard }) {
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchInputRef = useRef(null);

  const useSearchUI = practices.length >= 2;

  useEffect(() => {
    if (useSearchUI && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [useSearchUI]);

  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [];
    return practices.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [trimmedQuery, practices]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleSearchKeyDown = (e) => {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSelectPractice(filtered[activeIndex]);
    }
  };

  const createModal = showCreate && (
    <PracticeSetupWizard
      canOnboard={canOnboard}
      onPracticesUpdated={onPracticesUpdated}
      onClose={(practice) => { setShowCreate(false); if (practice) onSelectPractice(practice); }}
    />
  );

  if (useSearchUI) {
    return (
      <div className="practices-page practices-search-page">
        <div className="practices-search-container">
          <img src="/logo.png" alt="Atlas Billers" className="practices-search-logo" />
          <div className="practices-search-box">
            <svg className="practices-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="practices-search-input"
              placeholder="Search practices"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                className="practices-search-clear"
                onClick={() => { setQuery(''); searchInputRef.current?.focus(); }}
                aria-label="Clear search"
              >×</button>
            )}
          </div>

          {trimmedQuery && (
          <div className="practices-search-results">
            {filtered.length === 0 ? (
              <div className="practices-search-empty">No practices match "{query}"</div>
            ) : (
              filtered.map((p, idx) => (
                <div
                  key={p.id}
                  className={`practices-search-result${idx === activeIndex ? ' active' : ''}`}
                  onClick={() => onSelectPractice(p)}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <div className="practices-search-result-name">{p.name}</div>
                  <div className="practices-search-result-meta">
                    {p.npi && <span className="practices-search-result-npi">NPI: {p.npi}</span>}
                    {(p.city || p.state) && (
                      <span className="practices-search-result-loc">
                        {[p.city, p.state].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          )}
        </div>

        {isAdmin && (
          <div className="practices-fab-stack">
            {onGoToAdmin && (
              <button
                type="button"
                className="practices-fab practices-fab-secondary"
                onClick={onGoToAdmin}
                aria-label="Open admin page"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Admin</span>
              </button>
            )}
            <button
              type="button"
              className="practices-fab"
              onClick={() => setShowCreate(true)}
              aria-label="Add new practice"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add New Practice</span>
            </button>
          </div>
        )}

        {createModal}
      </div>
    );
  }

  return (
    <div className="practices-page">
      <div className="practices-container">
        <div className="practices-header">
          <div>
            <h1>Atlas</h1>
            <p>Select a practice to get started</p>
          </div>
        </div>

        <div className="practices-grid">
          {practices.map((p) => (
            <div key={p.id} className="practice-card" onClick={() => onSelectPractice(p)}>
              <div className="practice-card-header">
                <h3>{p.name}</h3>
                {p.npi && <span className="practice-npi">NPI: {p.npi}</span>}
              </div>
            </div>
          ))}

          {isAdmin && (
            <div className="practice-card add-card" onClick={() => setShowCreate(true)}>
              <div className="add-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
              </div>
              <span>Add New Practice</span>
            </div>
          )}
        </div>

        {isAdmin && onGoToAdmin && (
          <div className="practices-fab-stack">
            {onGoToAdmin && (
            <button
              type="button"
              className="practices-fab practices-fab-secondary"
              onClick={onGoToAdmin}
              aria-label="Open admin page"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>Admin</span>
            </button>
            )}
          </div>
        )}

        {createModal}
      </div>
    </div>
  );
}

export default PracticesPage;
